import React, { useState } from 'react';
import { Table, Button, Space, Tag, Tooltip, message, DatePicker, Select, Badge, Calendar } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  TableOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import HolidayModal from '../../../../components/modelpopup/HolidayModal';
import { useGetHolidaysQuery } from '../../../../services/settings.service';
import { HOLIDAYS } from '../../../../services/ENDPOINTS';
import { useDispatch } from 'react-redux';
import { toggleDelete } from '../../../../redux/slices/ui.slice';

const { Option } = Select;

const Holidays = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'calendar'
  const dispatch = useDispatch();

  // API Hooks
  const { data: holidaysData, isLoading, refetch } = useGetHolidaysQuery(selectedYear);

  const holidays = holidaysData?.data || [];

  // Handle Add
  const handleAdd = () => {
    setEditingHoliday(null);
    setModalVisible(true);
  };

  // Handle Edit
  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setModalVisible(true);
  };

  // Handle Delete
  const doDelete = (e, item) => {
    dispatch(
      toggleDelete({
        open: true,
        title: 'Holiday',
        url: `${HOLIDAYS}/${item.id}`,
        ele: e.currentTarget,
        parent: 'tr'
      })
    );
  };

  // Get holiday type color
  const getHolidayTypeColor = (type) => {
    const colors = {
      public: 'success',
      optional: 'processing',
      restricted: 'warning',
    };
    return colors[type] || 'default';
  };

  // Table Columns
  const columns = [
    {
      title: 'Holiday Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            <CalendarOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            {text}
          </div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 150,
      render: (date) => dayjs(date).format('DD MMM YYYY'),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: 'Day',
      dataIndex: 'date',
      key: 'day',
      width: 100,
      render: (date) => dayjs(date).format('dddd'),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => (
        <Tag color={getHolidayTypeColor(type)}>
          {type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Public'}
        </Tag>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 150,
      render: (location) => location || <Tag>All Locations</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => doDelete(e, record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Calendar cell renderer
  const dateCellRender = (value) => {
    const dateHolidays = holidays.filter(
      (holiday) => dayjs(holiday.date).format('YYYY-MM-DD') === value.format('YYYY-MM-DD')
    );

    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {dateHolidays.map((holiday) => (
          <li key={holiday.id}>
            <Badge
              color={getHolidayTypeColor(holiday.type)}
              text={
                <span
                  style={{ fontSize: '11px', cursor: 'pointer' }}
                  onClick={() => handleEdit(holiday)}
                >
                  {holiday.name}
                </span>
              }
            />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Breadcrumb */}
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Holidays</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/employee-dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Settings</li>
                </ul>
              </div>
              <div className="col-auto float-end ms-auto">
                <Link
                  to="#"
                  className="btn add-btn"
                  onClick={handleAdd}
                >
                  <i className="fa fa-plus" />
                  Add Holiday
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="card mb-0">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <h4 className="card-title mb-0">Holidays</h4>
                      <p className="text-muted mb-0">
                        Manage public holidays and company off days
                      </p>
                    </div>
                    <div className="col-auto">
                      <Space>
                        <Select
                          value={selectedYear}
                          onChange={setSelectedYear}
                          style={{ width: 100 }}
                        >
                          {[...Array(5)].map((_, i) => {
                            const year = dayjs().year() - 2 + i;
                            return (
                              <Option key={year} value={year}>
                                {year}
                              </Option>
                            );
                          })}
                        </Select>
                        <Button.Group>
                          <Button
                            type={viewMode === 'table' ? 'primary' : 'default'}
                            icon={<TableOutlined />}
                            onClick={() => setViewMode('table')}
                          >
                            Table
                          </Button>
                          <Button
                            type={viewMode === 'calendar' ? 'primary' : 'default'}
                            icon={<CalendarOutlined />}
                            onClick={() => setViewMode('calendar')}
                          >
                            Calendar
                          </Button>
                        </Button.Group>
                      </Space>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {viewMode === 'table' ? (
                    <Table
                      columns={columns}
                      dataSource={holidays}
                      rowKey="id"
                      loading={isLoading}
                      pagination={{
                        pageSize: 12,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} holidays`,
                      }}
                    />
                  ) : (
                    <Calendar
                      dateCellRender={dateCellRender}
                      value={dayjs().year(selectedYear)}
                      onSelect={(date) => {
                        const holiday = holidays.find(
                          (h) => dayjs(h.date).format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
                        );
                        if (!holiday) {
                          setEditingHoliday({ date: date.format('YYYY-MM-DD') });
                          setModalVisible(true);
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <HolidayModal
        visible={modalVisible}
        holiday={editingHoliday}
        onClose={() => {
          setModalVisible(false);
          setEditingHoliday(null);
        }}
        onSuccess={() => {
          setModalVisible(false);
          setEditingHoliday(null);
          refetch();
        }}
      />
    </>
  );
};

export default Holidays;
